import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Footer from "../Footer";
import Sidebar from "../sidebar";
import Topnav from "../TopNav";
import { API_URL } from "../config";
import Swal from 'sweetalert2';
import { ClipLoader } from 'react-spinners';

const Newsfeed = () => {
    const [news, setNews] = useState([]);
    const [filteredNews, setFilteredNews] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [show, setShow] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        file: null
    });

    const toggleSidebar = () => setShow(!show);

    /* ---------------- FETCH NEWS ---------------- */
    const fetchNews = async () => {
        const res = await fetch(`${API_URL}/newsfeed`);
        const data = await res.json();
        setNews(data);
        setFilteredNews(data);
    };

    useEffect(() => {
        fetchNews();
    }, []);

    useEffect(() => {
        const filtered = news.filter(n =>
            n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.content.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredNews(filtered);
    }, [searchTerm, news]);

    /* ---------------- ADD ---------------- */
    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('content', formData.content);
            if (formData.file) data.append('file', formData.file);

            await fetch(`${API_URL}/newsfeed/news`, {
                method: 'POST',
                body: data
            });

            setShowAddModal(false);
            fetchNews();
            Swal.fire("Success", "News added", "success");
        } catch {
            Swal.fire("Error", "Failed to add news", "error");
        }
        setIsLoading(false);
    };

    /* ---------------- EDIT ---------------- */
    const handleEdit = (item) => {
        setFormData({
            title: item.title,
            content: item.content,
            file: null
        });
        setCurrentId(item.id);
        setShowEditModal(true);
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();

        try {
            await fetch(`${API_URL}/newsfeed/${currentId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: formData.title,
                    content: formData.content
                })
            });

            setShowEditModal(false);
            fetchNews();
            Swal.fire("Updated", "News updated", "success");
        } catch {
            Swal.fire("Error", "Update failed", "error");
        }
    };

    /* ---------------- DELETE ---------------- */
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Delete?',
            showCancelButton: true
        });

        if (confirm.isConfirmed) {
            await fetch(`${API_URL}/newsfeed/${id}`, { method: 'DELETE' });
            fetchNews();
            Swal.fire("Deleted", "News removed", "success");
        }
    };

    return (
        <div className="wrapper">
            <Sidebar show={show} />
            <div className="main-panel">
                <Topnav toggleSidebar={toggleSidebar} />

                <div className="container">
                    <div>
                        &nbsp;
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                        <h3>Newsfeed ({filteredNews.length})</h3>
                        <button className="btn btn-dark btn-sm" onClick={() => setShowAddModal(true)}>
                            <FontAwesomeIcon icon={faPlus} /> Add News
                        </button>
                    </div>

                    <input
                        className="form-control mb-3"
                        placeholder="Search news..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />

                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Content</th>
                                <th>Image</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredNews.map(item => (
                                <tr key={item.id}>
                                    <td>{item.title}</td>
                                    <td>{item.content}</td>
                                    <td>
                                        {item.img && <img src={item.img} alt="" width="60" />}
                                    </td>
                                    <td>
                                        <button onClick={() => handleEdit(item)} className="btn btn-link">
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button onClick={() => handleDelete(item.id)} className="btn btn-link">
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Footer />
            </div>

            {/* ADD MODAL */}
            {showAddModal && (
                <div className="modal fade show d-block">
                    <div className="modal-dialog">
                        <form onSubmit={handleSubmitAdd} className="modal-content">
                            <div className="modal-header">
                                <h5>Add News</h5>
                                <button className="close" onClick={() => setShowAddModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <input className="form-control mb-2" placeholder="Title"
                                    onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                                <textarea className="form-control mb-2" placeholder="Content"
                                    onChange={e => setFormData({ ...formData, content: e.target.value })} required />
                                <input type="file" className="form-control"
                                    onChange={e => setFormData({ ...formData, file: e.target.files[0] })} />
                            </div>
                            <div className="modal-footer">
                                {isLoading ? <ClipLoader /> : <button className="btn btn-primary">Save</button>}
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {showEditModal && (
                <div className="modal fade show d-block">
                    <div className="modal-dialog">
                        <form onSubmit={handleSubmitEdit} className="modal-content">
                            <div className="modal-header">
                                <h5>Edit News</h5>
                                <button className="close" onClick={() => setShowEditModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <input className="form-control mb-2"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })} />
                                <textarea className="form-control"
                                    value={formData.content}
                                    onChange={e => setFormData({ ...formData, content: e.target.value })} />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-primary">Update</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
};

export default Newsfeed;
